import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingDown, TrendingUp, Save, RefreshCw, Loader2, Lock, Unlock, Copy,
  Plus, Pencil, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2,
  BarChart2, Building2, Info, Ruler, ChevronDown, ChevronUp,
} from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { API } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { PnLCharts } from '../components/PnLCharts';

// ─── Formatters ──────────────────────────────────────────────────────────────

/** Compact ₹ format using Indian thousands/lakhs notation */
const inr = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000)   return `${sign}₹${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000)     return `${sign}₹${(abs / 1000).toFixed(1)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
};

/** Revenue per sq. ft. — 2 decimal places with unit */
const sqftFmt = (n) =>
  n != null ? `₹${Number(n).toFixed(2)}/ft²` : '—';

/** Full Indian-locale ₹ format for KPI cards */
const inrFull = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const monthLabel     = (m) => { try { const [y, mo] = m.split('-'); return new Date(+y, +mo - 1, 1).toLocaleString('en-IN', { month: 'short', year: '2-digit' }); } catch { return m; } };
const fullMonthLabel = (m) => { try { const [y, mo] = m.split('-'); return new Date(+y, +mo - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' }); } catch { return m; } };

function prevMonthStr(m) { const [y, mo] = m.split('-').map(Number); return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`; }
function nextMonthStr(m) { const [y, mo] = m.split('-').map(Number); return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`; }
function currentMonthStr() { return new Date().toISOString().slice(0, 7); }

// ─── % change badge ───────────────────────────────────────────────────────────
const PctBadge = ({ current, prev }) => {
  if (!prev && prev !== 0) return <span className="text-[10px] text-slate-300 ml-1">—</span>;
  if (prev === 0 && current === 0) return null;
  if (prev === 0) return <span className="text-[10px] text-orange-500 ml-1">NEW</span>;
  const pct = ((current - prev) / Math.abs(prev)) * 100;
  if (Math.abs(pct) < 0.1) return <span className="text-[10px] text-slate-400 ml-1">=</span>;
  const up = pct > 0;
  // For costs: up = bad (orange), down = good (green)
  const cls = up ? 'text-orange-500' : 'text-emerald-600';
  return (
    <span className={`text-[10px] font-semibold ml-1 ${cls}`}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
export default function PnLPage() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';

  const [activeTab, setActiveTab]         = useState('monthly');

  // Monthly
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr());
  const [monthData, setMonthData]         = useState(null);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [localExpenses, setLocalExpenses] = useState([]);
  const [showExpenseRows, setShowExpenseRows] = useState(false);
  const [localOtherRev, setLocalOtherRev] = useState({});
  const [isDirty, setIsDirty]             = useState(false);
  const [copyConfirm, setCopyConfirm]     = useState(false);
  const [copying, setCopying]             = useState(false);
  const [locking, setLocking]             = useState(false);
  const autoSaveTimer                     = useRef(null);

  // Yearly
  const [yearlyYear, setYearlyYear]       = useState(new Date().getFullYear());
  const [yearlyData, setYearlyData]       = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);

  // Space Efficiency (sqft KPIs)
  const [sqftData, setSqftData]           = useState(null);
  const [sqftLoading, setSqftLoading]     = useState(false);
  const [showSpaceEff, setShowSpaceEff]   = useState(false);
  const [sqftGame, setSqftGame]           = useState('All');

  // Expense Heads
  const [heads, setHeads]                 = useState([]);
  const [headsLoading, setHeadsLoading]   = useState(false);
  const [headDialog, setHeadDialog]       = useState(false);
  const [editingHead, setEditingHead]     = useState(null);
  const [headForm, setHeadForm]           = useState({ name: '', is_active: true });
  const [savingHead, setSavingHead]       = useState(false);

  // ─── Fetch month ────────────────────────────────────────────────────────────
  const fetchMonth = useCallback(async (month) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pnl/month?month=${month}`);
      setMonthData(res.data);
      setLocalExpenses(res.data.expenses.map(e => ({ ...e, amounts: { ...(e.amounts || {}) } })));
      setLocalOtherRev(res.data.revenue?.other || {});
      setIsDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load P&L data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSqft = useCallback(async (month) => {
    setSqftLoading(true);
    try {
      const res = await axios.get(`${API}/pnl/sqft?month=${month}`);
      setSqftData(res.data);
    } catch { /* silent — sqft is supplementary */ }
    finally { setSqftLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'monthly') {
      fetchMonth(selectedMonth);
      fetchSqft(selectedMonth);
    }
  }, [selectedMonth, activeTab, fetchMonth, fetchSqft]);

  // ─── Fetch yearly ────────────────────────────────────────────────────────────
  const fetchYearly = useCallback(async (year) => {
    setYearlyLoading(true);
    try {
      const res = await axios.get(`${API}/pnl/yearly?year=${year}`);
      setYearlyData(res.data);
    } catch { toast.error('Failed to load yearly data'); }
    finally { setYearlyLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'yearly') fetchYearly(yearlyYear); }, [yearlyYear, activeTab, fetchYearly]);

  // ─── Fetch heads ─────────────────────────────────────────────────────────────
  const fetchHeads = useCallback(async () => {
    setHeadsLoading(true);
    try {
      const res = await axios.get(`${API}/pnl/expense-heads`);
      setHeads(res.data.heads);
    } catch { toast.error('Failed to load expense heads'); }
    finally { setHeadsLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'heads') fetchHeads(); }, [activeTab, fetchHeads]);

  // ─── Computed values ─────────────────────────────────────────────────────────
  const venues = useMemo(() => monthData?.venues || [], [monthData]);

  const getAmount        = useCallback((hid, v) => parseFloat(localExpenses.find(e => e.head_id === hid)?.amounts?.[v] || 0), [localExpenses]);
  const getOtherRev      = useCallback((v) => parseFloat(localOtherRev[v] || 0), [localOtherRev]);
  const computedRowTotal = useCallback((hid) => { const e = localExpenses.find(x => x.head_id === hid); if (!e?.is_active) return 0; return venues.reduce((s, v) => s + getAmount(hid, v), 0); }, [localExpenses, venues, getAmount]);
  const computedVenueTotal = useCallback((v) => localExpenses.filter(e => e.is_active).reduce((s, e) => s + getAmount(e.head_id, v), 0), [localExpenses, getAmount]);
  const computedGrandTotal = useMemo(() => venues.reduce((s, v) => s + computedVenueTotal(v), 0), [venues, computedVenueTotal]);

  const totalRevForVenue  = useCallback((v) => (monthData?.revenue?.day_book?.[v] || 0) + (monthData?.revenue?.bulk?.[v] || 0) + getOtherRev(v), [monthData, getOtherRev]);
  const totalRevGrand     = useMemo(() => venues.reduce((s, v) => s + totalRevForVenue(v), 0), [venues, totalRevForVenue]);
  const norForVenue       = useCallback((v) => totalRevForVenue(v) - computedVenueTotal(v), [totalRevForVenue, computedVenueTotal]);
  const norTotal          = useMemo(() => totalRevGrand - computedGrandTotal, [totalRevGrand, computedGrandTotal]);
  const operatingMarginPct = useMemo(() => totalRevGrand === 0 ? 0 : (norTotal / totalRevGrand) * 100, [norTotal, totalRevGrand]);

  // ─── Auto-save ────────────────────────────────────────────────────────────────
  const isLocked = monthData?.is_locked;

  const saveExpenses = useCallback(async () => {
    if (!isDirty || isLocked) return;
    setSaving(true);
    try {
      const entries = localExpenses.map(exp => ({
        head_id: exp.head_id,
        amounts: Object.fromEntries(venues.map(v => [v, getAmount(exp.head_id, v)])),
      }));
      const otherRevPayload = Object.fromEntries(venues.map(v => [v, getOtherRev(v)]));
      await axios.put(`${API}/pnl/month`, { month: selectedMonth, entries, other_revenue: otherRevPayload });
      setIsDirty(false);
      // Refresh for % change and server-computed totals
      const res = await axios.get(`${API}/pnl/month?month=${selectedMonth}`);
      setMonthData(res.data);
      // Keep local state in sync (don't reset inputs)
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-save failed');
    } finally {
      setSaving(false);
    }
  }, [isDirty, isLocked, localExpenses, localOtherRev, venues, selectedMonth, getAmount, getOtherRev]);

  const triggerAutoSave = useCallback(() => {
    if (isLocked) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveExpenses(), 800);
  }, [isLocked, saveExpenses]);

  const handleAmountChange = (hid, v, val) => {
    setLocalExpenses(prev => prev.map(e => e.head_id !== hid ? e : { ...e, amounts: { ...e.amounts, [v]: val === '' ? 0 : parseFloat(val) || 0 } }));
    setIsDirty(true);
  };
  const handleOtherRevChange = (v, val) => {
    setLocalOtherRev(prev => ({ ...prev, [v]: val === '' ? 0 : parseFloat(val) || 0 }));
    setIsDirty(true);
  };

  // ─── Manual save / copy / lock ───────────────────────────────────────────────
  const manualSave = async () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    setSaving(true);
    try {
      const entries = localExpenses.map(exp => ({
        head_id: exp.head_id,
        amounts: Object.fromEntries(venues.map(v => [v, getAmount(exp.head_id, v)])),
      }));
      const otherRevPayload = Object.fromEntries(venues.map(v => [v, getOtherRev(v)]));
      await axios.put(`${API}/pnl/month`, { month: selectedMonth, entries, other_revenue: otherRevPayload });
      toast.success('Saved');
      await fetchMonth(selectedMonth);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const copyPrevious = async (force = false) => {
    setCopying(true);
    try {
      await axios.post(`${API}/pnl/copy-previous`, { month: selectedMonth, force });
      toast.success('Copied from previous month');
      setCopyConfirm(false);
      await fetchMonth(selectedMonth);
    } catch (e) {
      if (e.response?.status === 409 && !force) { setCopyConfirm(true); setCopying(false); return; }
      toast.error(e.response?.data?.detail || 'Copy failed');
    } finally { setCopying(false); }
  };

  const toggleLock = async () => {
    if (!monthData) return;
    setLocking(true);
    try {
      const lock = !monthData.is_locked;
      await axios.post(`${API}/pnl/lock`, { month: selectedMonth, lock });
      toast.success(lock ? 'Month locked' : 'Month unlocked');
      await fetchMonth(selectedMonth);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLocking(false); }
  };

  // ─── Expense Heads CRUD ───────────────────────────────────────────────────────
  const openNewHead  = () => { setEditingHead(null); setHeadForm({ name: '', is_active: true }); setHeadDialog(true); };
  const openEditHead = (h) => { setEditingHead(h); setHeadForm({ name: h.name, is_active: h.is_active }); setHeadDialog(true); };
  const saveHead = async () => {
    if (!headForm.name.trim()) { toast.error('Name required'); return; }
    setSavingHead(true);
    try {
      editingHead
        ? await axios.put(`${API}/pnl/expense-heads/${editingHead.id}`, headForm)
        : await axios.post(`${API}/pnl/expense-heads`, headForm);
      toast.success(editingHead ? 'Updated' : 'Created');
      setHeadDialog(false); fetchHeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingHead(false); }
  };
  const toggleHead = async (h) => {
    try {
      await axios.patch(`${API}/pnl/expense-heads/${h.id}/toggle`);
      setHeads(prev => prev.map(x => x.id === h.id ? { ...x, is_active: !x.is_active } : x));
    } catch { toast.error('Failed'); }
  };

  // ─── Monthly Table ────────────────────────────────────────────────────────────
  const renderMonthlyTable = () => {
    if (!monthData) return null;
    const rev = monthData.revenue || {};
    const prevTotals = monthData.prev_row_totals || {};
    const colCount = venues.length + 2; // name + venues + row-total

    const cellCls = 'bg-inherit';
    const inputCls = 'w-full text-right bg-transparent border border-transparent hover:border-slate-200 focus:border-primary focus:bg-white focus:outline-none rounded px-2 py-1 font-mono text-sm transition-colors';

    return (
      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
        <table className="w-full text-sm border-collapse" style={{ minWidth: Math.max(640, 220 + venues.length * 145 + 140) }}>
          <thead>
            <tr className="bg-slate-800 text-white">
              {/* frozen first col */}
              <th className="px-4 py-3 text-left font-semibold sticky left-0 bg-slate-800 z-20 min-w-[200px]">
                Expense Head
              </th>
              {venues.map(v => (
                <th key={v} className="px-3 py-3 text-right font-semibold min-w-[130px] whitespace-nowrap">
                  <div className="text-xs opacity-80 leading-tight">{v.split(' ').slice(0, 2).join(' ')}</div>
                  <div className="text-[10px] opacity-50">{v.split(' ').slice(2).join(' ')}</div>
                </th>
              ))}
              {/* frozen last col */}
              <th className="px-3 py-3 text-right font-semibold min-w-[130px] bg-slate-700 sticky right-0 z-20">
                Row Total
              </th>
            </tr>
          </thead>

          <tbody>
            {/* ── Expenses list toggle (collapsed by default) ── */}
            <tr className="bg-slate-100 border-b cursor-pointer hover:bg-slate-200/70 transition-colors" onClick={() => setShowExpenseRows(v => !v)} data-testid="pnl-expenses-toggle">
              <td className="px-4 py-2 sticky left-0 z-10 bg-slate-100 font-semibold text-slate-700" colSpan={1}>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  {showExpenseRows ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Expenses List
                  <span className="text-[11px] font-normal text-slate-500">({localExpenses.filter(e => e.is_active).length} heads · {showExpenseRows ? 'click to collapse' : 'click to expand'})</span>
                </span>
              </td>
              <td colSpan={venues.length + 1} className="bg-slate-100" />
            </tr>
            {/* ── Expense rows ── */}
            {showExpenseRows && localExpenses.map((exp, idx) => {
              const rowTotal  = computedRowTotal(exp.head_id);
              const prevTotal = prevTotals[exp.head_id];
              const rowBg = !exp.is_active ? 'bg-slate-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50';
              return (
                <tr key={exp.head_id} className={`border-b ${rowBg} hover:bg-sky-50/30 transition-colors`} data-testid={`pnl-row-${exp.head_id}`}>
                  {/* frozen name col */}
                  <td className={`px-4 py-2 sticky left-0 z-10 border-r border-slate-100 ${rowBg}`}>
                    <span className={`font-medium ${!exp.is_active ? 'line-through opacity-50 text-slate-400' : ''}`}>{exp.head_name}</span>
                    {!exp.is_active && <span className="ml-1.5 text-[10px] bg-slate-200 text-slate-500 px-1.5 rounded">inactive</span>}
                    {exp.auto && <span className="ml-1.5 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 rounded" title="Sum of approved expense requests (by approval month)" data-testid="pnl-approved-works-badge">auto · {exp.request_count} approved</span>}
                  </td>
                  {venues.map(v => (
                    <td key={v} className={`px-2 py-1 text-right ${cellCls}`}>
                      {isLocked || !exp.is_active || exp.auto ? (
                        <span className="text-slate-600 px-2 font-mono">{inr(getAmount(exp.head_id, v))}</span>
                      ) : (
                        <input
                          type="number" min="0"
                          value={getAmount(exp.head_id, v) || ''}
                          onChange={e => handleAmountChange(exp.head_id, v, e.target.value)}
                          onBlur={triggerAutoSave}
                          placeholder="0"
                          className={inputCls}
                          data-testid={`pnl-cell-${exp.head_id}-${v.replace(/\s+/g, '-').toLowerCase()}`}
                        />
                      )}
                    </td>
                  ))}
                  {/* frozen row-total col */}
                  <td className={`px-3 py-2 text-right font-semibold bg-blue-50/40 sticky right-0 z-10 border-l border-blue-100`}>
                    <span className="font-mono text-slate-700">{inr(rowTotal)}</span>
                    {exp.is_active && <PctBadge current={rowTotal} prev={prevTotal} />}
                  </td>
                </tr>
              );
            })}

            {/* ── Total Fixed Costs ── */}
            <tr className="bg-blue-600 text-white font-bold text-sm border-t-2 border-blue-700">
              <td className="px-4 py-3 sticky left-0 bg-blue-600 z-10">TOTAL FIXED COSTS</td>
              {venues.map(v => (
                <td key={v} className="px-3 py-3 text-right font-mono" data-testid={`pnl-venue-total-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                  {inr(computedVenueTotal(v))}
                </td>
              ))}
              <td className="px-3 py-3 text-right font-mono bg-blue-700 sticky right-0 z-10" data-testid="pnl-grand-total">
                {inr(computedGrandTotal)}
              </td>
            </tr>

            {/* ── Revenue header ── */}
            <tr className="bg-slate-100 border-t-2 border-slate-300">
              <td colSpan={colCount} className="px-4 py-2">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Revenue</span>
              </td>
            </tr>

            {/* Day Book */}
            <tr className="border-b bg-emerald-50/30 hover:bg-emerald-50/60">
              <td className="px-4 py-2 sticky left-0 bg-emerald-50/30 z-10 font-medium text-emerald-800">Day Book Revenue</td>
              {venues.map(v => (
                <td key={v} className="px-3 py-2 text-right font-mono text-emerald-700" data-testid={`pnl-daybook-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                  {inr(rev.day_book?.[v] || 0)}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-emerald-700 bg-emerald-50/60 sticky right-0 z-10">
                {inr(rev.day_book_grand || 0)}
              </td>
            </tr>

            {/* Bulk */}
            <tr className="border-b bg-teal-50/30 hover:bg-teal-50/60">
              <td className="px-4 py-2 sticky left-0 bg-teal-50/30 z-10 font-medium text-teal-800">Bulk Slot Income</td>
              {venues.map(v => (
                <td key={v} className="px-3 py-2 text-right font-mono text-teal-700" data-testid={`pnl-bulk-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                  {inr(rev.bulk?.[v] || 0)}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-teal-700 bg-teal-50/60 sticky right-0 z-10">
                {inr(rev.bulk_grand || 0)}
              </td>
            </tr>

            {/* Other Revenue (editable, ±) */}
            <tr className="border-b bg-violet-50/30 hover:bg-violet-50/50">
              <td className="px-4 py-2 sticky left-0 bg-violet-50/30 z-10 font-medium text-violet-800">
                Other Revenue <span className="text-[10px] text-violet-500 font-normal">(±)</span>
              </td>
              {venues.map(v => (
                <td key={v} className="px-2 py-1 text-right" data-testid={`pnl-other-rev-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                  {isLocked ? (
                    <span className="font-mono text-violet-700 px-2">{inr(getOtherRev(v))}</span>
                  ) : (
                    <input
                      type="number"
                      value={getOtherRev(v) || ''}
                      onChange={e => handleOtherRevChange(v, e.target.value)}
                      onBlur={triggerAutoSave}
                      placeholder="0"
                      className="w-full text-right bg-transparent border border-transparent hover:border-violet-200 focus:border-violet-400 focus:bg-white focus:outline-none rounded px-2 py-1 font-mono text-sm transition-colors text-violet-700"
                      data-testid={`pnl-other-rev-input-${v.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                  )}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-violet-700 bg-violet-50/40 sticky right-0 z-10">
                {inr(venues.reduce((s, v) => s + getOtherRev(v), 0))}
              </td>
            </tr>

            {/* Total Revenue */}
            <tr className="bg-emerald-600 text-white font-bold">
              <td className="px-4 py-3 sticky left-0 bg-emerald-600 z-10">TOTAL REVENUE</td>
              {venues.map(v => (
                <td key={v} className="px-3 py-3 text-right font-mono" data-testid={`pnl-total-rev-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                  {inr(totalRevForVenue(v))}
                </td>
              ))}
              <td className="px-3 py-3 text-right font-mono bg-emerald-700 sticky right-0 z-10" data-testid="pnl-total-rev-grand">
                {inr(totalRevGrand)}
              </td>
            </tr>

            {/* NOR — Net Operating Result */}
            <tr className={`font-bold text-sm border-t-4 ${norTotal >= 0 ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400'}`}>
              <td className={`px-4 py-3 sticky left-0 z-10 ${norTotal >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                <div className="flex items-center gap-2">
                  {norTotal >= 0
                    ? <TrendingUp className="w-4 h-4 text-green-700" />
                    : <TrendingDown className="w-4 h-4 text-red-700" />}
                  <div>
                    <div className={norTotal >= 0 ? 'text-green-800' : 'text-red-800'}>NOR</div>
                    <div className="text-[10px] font-normal opacity-70">Net Operating Result</div>
                  </div>
                </div>
              </td>
              {venues.map(v => {
                const nm = norForVenue(v);
                return (
                  <td key={v} className={`px-3 py-3 text-right font-mono ${nm >= 0 ? 'text-green-700' : 'text-red-700'}`}
                    data-testid={`pnl-nor-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                    {inr(nm)}
                  </td>
                );
              })}
              <td className={`px-3 py-3 text-right font-mono sticky right-0 z-10 ${norTotal >= 0 ? 'text-green-800 bg-green-200' : 'text-red-800 bg-red-200'}`}
                data-testid="pnl-nor-grand">
                {inr(norTotal)}
              </td>
            </tr>

            {/* ── Revenue / sq. ft. ─────────────────────────────── */}
            {sqftData?.has_area_data && (
              <>
                <tr className="bg-indigo-50 border-t border-indigo-200 text-xs font-semibold" data-testid="pnl-rev-sqft-row">
                  <td className="px-4 py-2.5 sticky left-0 bg-indigo-50 z-10">
                    <div className="flex items-center gap-1.5 text-indigo-800">
                      <Ruler className="w-3.5 h-3.5" />
                      <div>
                        <div>Revenue / sq. ft.</div>
                        <div className="text-[10px] font-normal text-indigo-500">Total rev ÷ Revenue-Generating Area</div>
                      </div>
                    </div>
                  </td>
                  {venues.map(v => {
                    const vs = sqftData.venue_sqft?.[v];
                    const val = vs?.rev_per_sqft;
                    return (
                      <td key={v} className="px-3 py-2.5 text-right font-mono text-indigo-700"
                        data-testid={`pnl-rev-sqft-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                        {val != null ? sqftFmt(val) : <span className="text-slate-300 font-normal text-[10px]">no area</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-right font-mono text-indigo-800 bg-indigo-100 sticky right-0 z-10">
                    {sqftData.aggregate?.rev_per_sqft != null ? sqftFmt(sqftData.aggregate.rev_per_sqft) : '—'}
                  </td>
                </tr>

                <tr className="bg-violet-50 border-b border-violet-200 text-xs font-semibold" data-testid="pnl-nor-sqft-row">
                  <td className="px-4 py-2.5 sticky left-0 bg-violet-50 z-10">
                    <div className="flex items-center gap-1.5 text-violet-800">
                      <Ruler className="w-3.5 h-3.5" />
                      <div>
                        <div>NOR / sq. ft.</div>
                        <div className="text-[10px] font-normal text-violet-500">NOR ÷ Revenue-Generating Area</div>
                      </div>
                    </div>
                  </td>
                  {venues.map(v => {
                    const vs = sqftData.venue_sqft?.[v];
                    const val = vs?.nor_per_sqft;
                    return (
                      <td key={v} className={`px-3 py-2.5 text-right font-mono ${val != null ? (val >= 0 ? 'text-violet-700' : 'text-red-600') : ''}`}
                        data-testid={`pnl-nor-sqft-${v.replace(/\s+/g, '-').toLowerCase()}`}>
                        {val != null ? sqftFmt(val) : <span className="text-slate-300 font-normal text-[10px]">no area</span>}
                      </td>
                    );
                  })}
                  <td className={`px-3 py-2.5 text-right font-mono sticky right-0 z-10 ${
                    sqftData.aggregate?.nor_per_sqft != null
                      ? sqftData.aggregate.nor_per_sqft >= 0 ? 'text-violet-800 bg-violet-100' : 'text-red-700 bg-red-100'
                      : 'bg-violet-100'}`}>
                    {sqftData.aggregate?.nor_per_sqft != null ? sqftFmt(sqftData.aggregate.nor_per_sqft) : '—'}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ─── Yearly Table ─────────────────────────────────────────────────────────────
  const renderYearlyTable = () => {
    if (!yearlyData) return null;
    const { months, venues: yVenues, year_totals } = yearlyData;

    // Only 3 rows per venue — no bifurcation of revenue
    const ROW_DEFS = [
      { key: 'fixed_costs',   label: 'Fixed Costs',   color: 'text-blue-700',      bg: 'bg-blue-50' },
      { key: 'total_revenue', label: 'Total Revenue', color: 'text-emerald-800',   bg: 'bg-emerald-100/60' },
      { key: 'net_margin',    label: 'NOR',           color: '',                    bg: '' },
    ];

    const venueAreas = yearlyData.venue_areas || {};
    const allGroups  = [...yVenues, 'TOTAL'];

    // For each month, compute rev_per_sqft per venue from yearly data
    const monthRevSqft = (monthObj, venue) => {
      const rga = (venueAreas[venue] || {}).revenue_generating_area;
      if (!rga || rga <= 0 || monthObj.is_future) return null;
      const rev = (monthObj.total_revenue || {})[venue] || 0;
      return rev / rga;
    };
    const hasYearlyAreaData = yVenues.some(v => (venueAreas[v] || {}).revenue_generating_area);

    return (
      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
        <table className="w-full text-xs border-collapse" style={{ minWidth: 900 }}>
          <thead>
            <tr className="bg-slate-800 text-white">
              <th className="px-3 py-3 text-left sticky left-0 bg-slate-800 z-20 min-w-[170px]">Venue / Metric</th>
              {months.map(m => (
                <th key={m.month} className={`px-2 py-3 text-right min-w-[78px] ${m.is_future ? 'opacity-30' : ''}`}>
                  {m.label}
                  {m.is_locked && <Lock className="w-2.5 h-2.5 inline ml-1 opacity-60" />}
                </th>
              ))}
              <th className="px-2 py-3 text-right min-w-[90px] bg-slate-700 font-bold sticky right-0 z-20">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {allGroups.map((venue) => {
              const isTotal = venue === 'TOTAL';
              const vKey    = isTotal ? '_total' : venue;
              return [
                ...ROW_DEFS.map((row, rIdx) => {
                  const isFirst    = rIdx === 0;
                  const isNOR      = row.key === 'net_margin';
                  const yearVal    = (year_totals[row.key] || {})[vKey] || 0;
                  return (
                    <tr key={`${venue}-${row.key}`}
                      className={`border-b ${isTotal ? 'bg-slate-100 font-bold' : isFirst ? 'border-t-2 border-slate-300' : ''} ${row.bg}`}>
                      {/* frozen left col */}
                      <td className={`px-3 py-1.5 sticky left-0 z-10 ${isTotal ? 'bg-slate-100' : row.bg || 'bg-white'}`}>
                        {isFirst && (
                          <div className={`font-semibold text-[11px] mb-0.5 ${isTotal ? 'text-slate-900' : 'text-slate-700'}`}>
                            {isTotal ? 'ALL VENUES' : venue.length > 24 ? venue.slice(0, 22) + '…' : venue}
                          </div>
                        )}
                        <div className={`text-[11px] ${row.color || (isNOR ? 'text-slate-600' : 'text-slate-500')}`}>{row.label}</div>
                      </td>
                      {months.map(m => {
                        const val = m.is_future ? null : (m[row.key] || {})[vKey] || 0;
                        const isNeg = isNOR && val !== null && val < 0;
                        return (
                          <td key={m.month}
                            className={`px-2 py-1.5 text-right font-mono ${m.is_future ? 'text-slate-200' : isNeg ? 'text-red-600' : isNOR ? 'text-emerald-700' : 'text-slate-700'}`}
                            data-testid={`yearly-${row.key}-${venue.replace(/\s+/g, '-')}-${m.month}`}>
                            {m.is_future ? '—' : val === 0 ? <span className="text-slate-300">—</span> : inr(val)}
                          </td>
                        );
                      })}
                      {/* frozen right col */}
                      <td className={`px-2 py-1.5 text-right font-mono font-bold sticky right-0 z-10 ${
                        isNOR
                          ? yearVal >= 0 ? 'text-emerald-700 bg-emerald-100' : 'text-red-700 bg-red-100'
                          : row.key === 'fixed_costs' ? 'text-blue-700 bg-blue-100' : 'text-slate-700 bg-slate-100'
                      }`}>
                        {inr(yearVal)}
                      </td>
                    </tr>
                  );
                }),
                // Rev/sqft row (only for individual venues with area data, not TOTAL)
                !isTotal && hasYearlyAreaData && (venueAreas[venue] || {}).revenue_generating_area ? (
                  <tr key={`${venue}-rev-sqft`} className="border-b bg-indigo-50/50 text-[11px]">
                    <td className="px-3 py-1.5 sticky left-0 bg-indigo-50/50 z-10">
                      <div className="flex items-center gap-1 text-indigo-600">
                        <Ruler className="w-3 h-3" />
                        <span>Rev / sq. ft.</span>
                      </div>
                    </td>
                    {months.map(m => {
                      const v = monthRevSqft(m, venue);
                      return (
                        <td key={m.month} className={`px-2 py-1.5 text-right font-mono ${m.is_future ? 'text-slate-200' : 'text-indigo-600'}`}
                          data-testid={`yearly-rev-sqft-${venue.replace(/\s+/g, '-')}-${m.month}`}>
                          {m.is_future ? '—' : v != null ? sqftFmt(v) : <span className="text-slate-300">—</span>}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-right font-mono font-bold sticky right-0 z-10 text-indigo-700 bg-indigo-100">
                      {(() => {
                        const rga = (venueAreas[venue] || {}).revenue_generating_area;
                        const yr  = (year_totals.total_revenue || {})[venue] || 0;
                        return rga && rga > 0 ? sqftFmt(yr / rga) : '—';
                      })()}
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="space-y-5 p-2 sm:p-0" data-testid="pnl-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart2 className="w-7 h-7 text-primary" /> Profit &amp; Loss
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">Venue-wise fixed costs, revenue &amp; NOR</p>
          </div>
          {saving && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full" data-testid="pnl-autosave-indicator">
              <Loader2 className="w-3 h-3 animate-spin" /> Auto-saving…
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="h-10 bg-slate-100">
            <TabsTrigger value="monthly" data-testid="pnl-tab-monthly" className="gap-1.5">
              <TrendingDown className="w-4 h-4" /> Monthly P&amp;L
            </TabsTrigger>
            <TabsTrigger value="yearly" data-testid="pnl-tab-yearly" className="gap-1.5">
              <BarChart2 className="w-4 h-4" /> Yearly View
            </TabsTrigger>
            <TabsTrigger value="heads" data-testid="pnl-tab-heads" className="gap-1.5">
              <Building2 className="w-4 h-4" /> Expense Heads
            </TabsTrigger>
          </TabsList>

          {/* ════════════════════ TAB: Monthly P&L ════════════════════ */}
          <TabsContent value="monthly" className="space-y-4">
            {/* Controls bar */}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="icon" className="h-9 w-9 border" onClick={() => setSelectedMonth(prevMonthStr(selectedMonth))} data-testid="pnl-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <input type="month" value={selectedMonth} max={currentMonthStr()}
                onChange={e => setSelectedMonth(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="pnl-month-input" />
              <Button variant="ghost" size="icon" className="h-9 w-9 border"
                onClick={() => setSelectedMonth(nextMonthStr(selectedMonth))}
                disabled={selectedMonth >= currentMonthStr()} data-testid="pnl-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <div className="flex gap-1.5 ml-1">
                {[{ label: 'This Month', val: currentMonthStr() }, { label: 'Last Month', val: prevMonthStr(currentMonthStr()) }].map(p => (
                  <Button key={p.val} size="sm" variant={selectedMonth === p.val ? 'default' : 'outline'}
                    onClick={() => setSelectedMonth(p.val)} className="h-8 text-xs px-3">{p.label}</Button>
                ))}
              </div>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                {monthData?.copied_from && (
                  <span className="text-[11px] text-slate-400 flex items-center gap-1">
                    <Copy className="w-3 h-3" /> from {monthLabel(monthData.copied_from)}
                  </span>
                )}
                {isAdmin && monthData && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => copyPrevious(false)}
                      disabled={copying} className="h-8 text-xs gap-1" data-testid="pnl-copy-prev-btn">
                      <Copy className="w-3.5 h-3.5" /> Copy Prev
                    </Button>
                    <Button size="sm" variant={isLocked ? 'default' : 'outline'}
                      onClick={toggleLock} disabled={locking}
                      className={`h-8 text-xs gap-1 ${isLocked ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                      data-testid="pnl-lock-btn">
                      {locking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isLocked ? <><Unlock className="w-3.5 h-3.5" /> Unlock</> : <><Lock className="w-3.5 h-3.5" /> Lock</>}
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => fetchMonth(selectedMonth)} className="h-8" data-testid="pnl-refresh-btn">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                {!isLocked && isDirty && (
                  <Button size="sm" onClick={manualSave} disabled={saving}
                    className="h-8 bg-primary text-white gap-1 text-xs" data-testid="pnl-save-btn">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save
                  </Button>
                )}
              </div>
            </div>

            {/* Status */}
            {monthData && (
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-bold text-slate-800">{fullMonthLabel(selectedMonth)}</h2>
                {isLocked && <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-800 border border-amber-300 px-2.5 py-1 rounded-full"><Lock className="w-3 h-3" /> Locked by {monthData.locked_by}</span>}
                {isDirty && !isLocked && <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full"><Info className="w-3 h-3" /> Unsaved changes</span>}
              </div>
            )}

            {/* KPI cards (4-col) */}
            {monthData && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="p-4 bg-blue-50 border-blue-200" data-testid="pnl-kpi-costs">
                  <div className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-1">Fixed Costs</div>
                  <div className="text-xl font-bold text-blue-800 font-mono">{inr(computedGrandTotal)}</div>
                  <div className="text-[11px] text-blue-500 mt-0.5">{inrFull(computedGrandTotal)}</div>
                </Card>
                <Card className="p-4 bg-emerald-50 border-emerald-200" data-testid="pnl-kpi-revenue">
                  <div className="text-xs text-emerald-600 font-semibold uppercase tracking-wide mb-1">Total Revenue</div>
                  <div className="text-xl font-bold text-emerald-800 font-mono">{inr(totalRevGrand)}</div>
                  <div className="text-[11px] text-emerald-500 mt-0.5">
                    DayBook {inr(monthData.revenue?.day_book_grand || 0)} + Bulk {inr(monthData.revenue?.bulk_grand || 0)}
                    {monthData.revenue?.other_grand !== 0 && ` + Other ${inr(monthData.revenue?.other_grand || 0)}`}
                  </div>
                </Card>
                <Card className={`p-4 border ${norTotal >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="pnl-kpi-nor">
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${norTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    NOR <span className="normal-case font-normal opacity-70">(Net Oper. Result)</span>
                  </div>
                  <div className={`text-xl font-bold font-mono ${norTotal >= 0 ? 'text-green-800' : 'text-red-800'}`}>{inr(norTotal)}</div>
                  <div className={`text-[11px] mt-0.5 ${norTotal >= 0 ? 'text-green-500' : 'text-red-500'}`}>{inrFull(norTotal)}</div>
                </Card>
                <Card className={`p-4 border ${operatingMarginPct >= 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-rose-50 border-rose-200'}`} data-testid="pnl-kpi-margin-pct">
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${operatingMarginPct >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                    Operating Margin %
                  </div>
                  <div className={`text-xl font-bold font-mono flex items-center gap-1 ${operatingMarginPct >= 0 ? 'text-indigo-800' : 'text-rose-800'}`}>
                    {operatingMarginPct >= 0 ? '+' : ''}{operatingMarginPct.toFixed(1)}%
                  </div>
                  <div className={`text-[11px] mt-0.5 ${operatingMarginPct >= 0 ? 'text-indigo-500' : 'text-rose-500'}`}>
                    NOR ÷ Revenue × 100
                  </div>
                </Card>
              </div>
            )}

            {/* Main table */}
            {loading ? (
              <Card className="p-16 text-center text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin inline mb-2 text-primary" />
                <div>Loading P&amp;L data…</div>
              </Card>
            ) : monthData ? renderMonthlyTable() : null}

            {/* ── Space Efficiency (per venue + per venue+game) ── */}
            {sqftData?.has_area_data && !loading && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowSpaceEff(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-xl transition-colors"
                  data-testid="space-eff-toggle"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800">
                    <Ruler className="w-4 h-4" />
                    Space Efficiency
                    <span className="text-xs font-normal text-indigo-500 ml-1">
                      Revenue &amp; NOR per sq. ft. · Per venue &amp; game breakdown
                    </span>
                    {sqftData.aggregate?.rev_per_sqft != null && (
                      <span className="px-2 py-0.5 bg-indigo-200 text-indigo-800 rounded-full text-xs font-bold ml-2">
                        Blended: {sqftFmt(sqftData.aggregate.rev_per_sqft)} / ft²
                      </span>
                    )}
                  </div>
                  {showSpaceEff ? <ChevronUp className="w-4 h-4 text-indigo-500" /> : <ChevronDown className="w-4 h-4 text-indigo-500" />}
                </button>

                {showSpaceEff && (
                  <div className="space-y-4 pt-1" data-testid="space-eff-panel">
                    {/* ── Venue-level summary cards ─── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(sqftData.venue_sqft || {}).map(([v, vs]) => {
                        if (!vs.revenue_generating_area) return null;
                        return (
                          <div key={v} className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                            <p className="text-sm font-semibold text-slate-800 mb-3 truncate">{v}</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                              <div>
                                <p className="text-slate-400 mb-0.5">Revenue Area</p>
                                <p className="font-medium text-slate-700">{vs.revenue_generating_area?.toLocaleString('en-IN')} sq. ft.</p>
                              </div>
                              <div>
                                <p className="text-slate-400 mb-0.5">Total Revenue</p>
                                <p className="font-medium text-emerald-700">{inr(vs.total_revenue)}</p>
                              </div>
                              <div>
                                <p className="text-slate-400 mb-0.5">Rev / sq. ft.</p>
                                <p className="font-bold text-indigo-700 text-sm">{sqftFmt(vs.rev_per_sqft)}</p>
                              </div>
                              <div>
                                <p className="text-slate-400 mb-0.5">NOR / sq. ft.</p>
                                <p className={`font-bold text-sm ${vs.nor_per_sqft >= 0 ? 'text-violet-700' : 'text-red-600'}`}>
                                  {sqftFmt(vs.nor_per_sqft)}
                                </p>
                              </div>
                              {vs.roi_pct != null && (
                                <div className="col-span-2">
                                  <p className="text-slate-400 mb-0.5">Monthly ROI</p>
                                  <p className={`font-bold text-sm ${vs.roi_pct >= 0 ? 'text-amber-700' : 'text-red-600'}`}>{vs.roi_pct.toFixed(2)}%</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* ── Per-Venue per-Game breakdown table ─── */}
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-3">
                        <p className="text-sm font-semibold text-slate-700">Revenue per sq. ft. by Venue &amp; Game</p>
                        {/* Game filter */}
                        <div className="flex flex-wrap gap-1.5 ml-auto">
                          {['All', ...new Set(
                            Object.values(sqftData.game_sqft || {}).flatMap(g => Object.keys(g))
                          )].map(g => (
                            <button key={g} onClick={() => setSqftGame(g)}
                              data-testid={`sqft-game-filter-${g}`}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                sqftGame === g
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-300'
                              }`}>
                              {g}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="game-sqft-table">
                          <thead>
                            <tr className="border-b text-xs text-slate-500 uppercase tracking-wide bg-slate-50">
                              <th className="px-4 py-2.5 text-left font-medium">Venue</th>
                              <th className="px-4 py-2.5 text-left font-medium">Game</th>
                              <th className="px-4 py-2.5 text-right font-medium">Booking Revenue</th>
                              <th className="px-4 py-2.5 text-right font-medium">Rev / sq. ft.</th>
                              <th className="px-4 py-2.5 text-right font-medium">% of Venue Rev</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rows = [];
                              Object.entries(sqftData.game_sqft || {}).forEach(([vn, games]) => {
                                const venueTotal = (sqftData.venue_sqft?.[vn] || {}).total_revenue || 0;
                                const rga = (sqftData.venue_sqft?.[vn] || {}).revenue_generating_area;
                                Object.entries(games).forEach(([gm, gd], i) => {
                                  if (sqftGame !== 'All' && gm !== sqftGame) return;
                                  const pct = venueTotal > 0 ? ((gd.revenue / venueTotal) * 100).toFixed(1) : null;
                                  rows.push(
                                    <tr key={`${vn}-${gm}`} className="border-b hover:bg-slate-50"
                                      data-testid={`game-sqft-row-${vn.replace(/\s+/g,'-')}-${gm}`}>
                                      <td className="px-4 py-2.5 font-medium text-slate-800">{i === 0 ? vn : ''}</td>
                                      <td className="px-4 py-2.5">
                                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">{gm}</span>
                                      </td>
                                      <td className="px-4 py-2.5 text-right font-mono text-emerald-700 font-medium">{inr(gd.revenue)}</td>
                                      <td className="px-4 py-2.5 text-right font-mono text-indigo-700 font-semibold">
                                        {gd.rev_per_sqft != null ? sqftFmt(gd.rev_per_sqft) : <span className="text-slate-300 text-xs">no area</span>}
                                      </td>
                                      <td className="px-4 py-2.5 text-right text-slate-500 text-xs">{pct ? `${pct}%` : '—'}</td>
                                    </tr>
                                  );
                                });
                              });
                              if (rows.length === 0) {
                                return <tr><td colSpan={5} className="py-8 text-center text-slate-400 text-sm">No booking data for the selected filter</td></tr>;
                              }
                              return rows;
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Audit */}
            {monthData && (
              <div className="text-[11px] text-slate-400 flex flex-wrap gap-4 pt-1">
                {monthData.created_by && <span>Created by {monthData.created_by} · {monthData.created_at?.slice(0, 10)}</span>}
                {monthData.updated_by && <span>Updated by {monthData.updated_by} · {monthData.updated_at?.slice(0, 10)}</span>}
              </div>
            )}
          </TabsContent>

          {/* ════════════════════ TAB: Yearly View ════════════════════ */}
          <TabsContent value="yearly" className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-9 w-9 border" onClick={() => setYearlyYear(y => y - 1)} data-testid="pnl-prev-year"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="font-bold text-xl text-slate-800 px-3" data-testid="pnl-year-display">{yearlyYear}</div>
              <Button variant="ghost" size="icon" className="h-9 w-9 border" onClick={() => setYearlyYear(y => y + 1)}
                disabled={yearlyYear >= new Date().getFullYear()} data-testid="pnl-next-year"><ChevronRight className="w-4 h-4" /></Button>
              <div className="flex gap-1.5 ml-2">
                {[{ l: 'This Year', v: new Date().getFullYear() }, { l: 'Last Year', v: new Date().getFullYear() - 1 }].map(({ l, v }) => (
                  <Button key={v} size="sm" variant={yearlyYear === v ? 'default' : 'outline'} onClick={() => setYearlyYear(v)} className="h-8 text-xs px-3">{l}</Button>
                ))}
              </div>
              <Button size="sm" variant="outline" className="h-8 ml-2" onClick={() => fetchYearly(yearlyYear)} data-testid="pnl-yearly-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${yearlyLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {yearlyLoading ? (
              <Card className="p-16 text-center text-slate-400"><Loader2 className="w-8 h-8 animate-spin inline text-primary" /></Card>
            ) : yearlyData ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Total Fixed Costs', key: 'fixed_costs', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
                    { label: 'Total Revenue', key: 'total_revenue', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
                    { label: 'NOR (Net Oper. Result)', key: 'net_margin', color: '', bg: '' },
                  ].map(({ label, key, color, bg }) => {
                    const val = (yearlyData.year_totals[key] || {})._total || 0;
                    const isNOR = key === 'net_margin';
                    return (
                      <Card key={key} className={`p-4 border ${isNOR ? (val >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200') : bg}`} data-testid={`pnl-yearly-kpi-${key}`}>
                        <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isNOR ? (val >= 0 ? 'text-green-600' : 'text-red-600') : color}`}>{label}</div>
                        <div className={`text-xl font-bold font-mono ${isNOR ? (val >= 0 ? 'text-green-800' : 'text-red-800') : color}`}>{inr(val)}</div>
                        <div className="text-[11px] text-slate-400">{inrFull(val)}</div>
                      </Card>
                    );
                  })}
                </div>
                {renderYearlyTable()}
                <PnLCharts yearlyData={yearlyData} venueAreas={yearlyData.venue_areas || {}} />
              </>
            ) : null}
          </TabsContent>

          {/* ════════════════════ TAB: Expense Heads ════════════════════ */}
          <TabsContent value="heads" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-800">Expense Heads Master</h2>
                <p className="text-xs text-slate-500 mt-0.5">Inactive heads are hidden from new months but historical data is preserved.</p>
              </div>
              <Button size="sm" onClick={openNewHead} className="gap-1.5" data-testid="pnl-add-head-btn">
                <Plus className="w-3.5 h-3.5" /> Add Expense Head
              </Button>
            </div>
            {headsLoading ? (
              <Card className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></Card>
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600">#</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-600">Expense Head</th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-600">Status</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heads.map((h, idx) => (
                      <tr key={h.id} className="border-b hover:bg-slate-50" data-testid={`pnl-head-row-${h.id}`}>
                        <td className="px-4 py-2.5 text-slate-400 text-xs">{idx + 1}</td>
                        <td className="px-4 py-2.5">
                          <span className={`font-medium ${!h.is_active ? 'line-through text-slate-400' : 'text-slate-800'}`}>{h.name}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${h.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {h.is_active && <CheckCircle2 className="w-3 h-3" />}{h.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Switch checked={h.is_active} onCheckedChange={() => toggleHead(h)} data-testid={`pnl-head-toggle-${h.id}`} />
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditHead(h)} data-testid={`pnl-head-edit-${h.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {heads.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No expense heads.</td></tr>}
                  </tbody>
                </table>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Copy confirm dialog */}
        <Dialog open={copyConfirm} onOpenChange={setCopyConfirm}>
          <DialogContent className="sm:max-w-[420px]" data-testid="pnl-copy-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Overwrite &amp; Copy?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              Data already exists for <b>{fullMonthLabel(selectedMonth)}</b>. This will overwrite it with values from <b>{fullMonthLabel(prevMonthStr(selectedMonth))}</b>.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCopyConfirm(false)}>Cancel</Button>
              <Button onClick={() => copyPrevious(true)} disabled={copying} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="pnl-copy-confirm-btn">
                {copying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />} Overwrite &amp; Copy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Head dialog */}
        <Dialog open={headDialog} onOpenChange={setHeadDialog}>
          <DialogContent className="sm:max-w-[380px]" data-testid="pnl-head-dialog">
            <DialogHeader>
              <DialogTitle>{editingHead ? 'Edit Expense Head' : 'Add Expense Head'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Name</Label>
                <Input autoFocus value={headForm.name} onChange={e => setHeadForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Security, Software Subscriptions" data-testid="pnl-head-name-input" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={headForm.is_active} onCheckedChange={v => setHeadForm(p => ({ ...p, is_active: v }))} data-testid="pnl-head-active-switch" />
                Active (appears in monthly tables)
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHeadDialog(false)}>Cancel</Button>
              <Button onClick={saveHead} disabled={savingHead} data-testid="pnl-head-save-btn">
                {savingHead ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
